// src/components/presets/BannerCard.tsx
'use client'

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Shield, X, Check } from 'lucide-react'
import type { BannerProps } from '../Banner'
import { getAnimationValues, getBorderRadiusValue } from '../../utils/style-helpers'
import { getCombinedStyles } from '../../utils/positioning'
import { getCategoryLabel, getCategoryShortDescription } from '../../utils/translation-helpers'

function CategoryCheckbox({
  checked,
  disabled,
  onChange,
  label,
  description,
  accentColor,
}: {
  checked: boolean
  disabled?: boolean
  onChange?: () => void
  label: string
  description: string
  accentColor: string
}) {
  const id = React.useId()

  return (
    <div
      style={{
        display: 'flex',
        gap: '10px',
        padding: '10px',
        backgroundColor: checked ? `${accentColor}06` : '#fafafa',
        borderRadius: '6px',
        border: checked ? `1px solid ${accentColor}20` : '1px solid #f0f0f0',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.2s',
      }}
    >
      <div style={{ position: 'relative', marginTop: '1px' }}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={onChange}
          style={{ position: 'absolute', opacity: 0 }}
        />
        <label htmlFor={id} style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}>
          <div
            style={{
              width: '16px',
              height: '16px',
              borderRadius: '3px',
              border: checked ? `2px solid ${accentColor}` : '2px solid #d1d5db',
              backgroundColor: checked ? accentColor : '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
            }}
          >
            {checked && (
              <Check style={{ width: '10px', height: '10px', color: '#ffffff', strokeWidth: 3 }} />
            )}
          </div>
        </label>
      </div>
      <label htmlFor={id} style={{ flex: 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#111827', marginBottom: '2px' }}>
          {label}
          {disabled && (
            <span
              style={{ fontSize: '10px', color: '#9ca3af', fontWeight: 400, marginLeft: '4px' }}
            >
              (required)
            </span>
          )}
        </div>
        <div style={{ fontSize: '10px', color: '#6b7280', lineHeight: 1.3 }}>{description}</div>
      </label>
    </div>
  )
}

export function BannerCard(props: BannerProps) {
  const {
    config,
    texts,
    categories,
    consent,
    onAccept,
    onDecline,
    onClose,
    onConsentChange,
    accentColor,
    style,
  } = props
  const {
    closeButton,
    showSettingsButton,
    showRejectLink,
    descriptionMode = 'short',
    language = 'en',
  } = config
  const [showSettings, setShowSettings] = useState(false)

  const animationValues = getAnimationValues(style.animation.direction)
  const description =
    descriptionMode === 'long'
      ? texts.longDescription || texts.description
      : texts.shortDescription || texts.description

  const containerStyles = getCombinedStyles(style.position, 'sm', {
    backgroundColor: style.background,
    borderRadius: getBorderRadiusValue(style.borderRadius),
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden',
  })

  const buttonBorderRadiusValue = style.borderRadius === 'sharp' ? '0' : '10px'

  const handleSaveSettings = () => {
    setShowSettings(false)
    onAccept()
  }

  return (
    <motion.div
      initial={style.animation.enabled ? animationValues.initial : { opacity: 1 }}
      animate={style.animation.enabled ? animationValues.animate : { opacity: 1 }}
      exit={style.animation.enabled ? animationValues.exit : { opacity: 0 }}
      transition={{ duration: style.animation.duration }}
      style={containerStyles}
    >
      <div style={{ height: '8px', backgroundColor: accentColor }} />

      <div style={{ padding: '24px', position: 'relative' }}>
        {closeButton && (
          <button
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
              onClose()
            }}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              zIndex: 10,
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            aria-label={texts.closeButton}
          >
            <X style={{ width: '14px', height: '14px', color: '#9ca3af' }} />
          </button>
        )}

        <div style={{ paddingRight: '32px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
            <Shield style={{ width: '18px', height: '18px', color: accentColor }} />
            <h3
              style={{
                fontSize: '15px',
                fontWeight: 700,
                color: '#111827',
                margin: 0,
                lineHeight: 1.3,
              }}
            >
              {texts.heading}
            </h3>
          </div>
          <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.5, margin: 0 }}>
            {description}
          </p>
        </div>

        <AnimatePresence>
          {showSettings && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ overflow: 'hidden', marginBottom: '16px' }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {categories.map(category => (
                  <CategoryCheckbox
                    key={category.id}
                    checked={consent[category.id] !== false}
                    disabled={category.required}
                    onChange={() =>
                      !category.required && onConsentChange?.(category.id, !consent[category.id])
                    }
                    label={getCategoryLabel(category, language)}
                    description={getCategoryShortDescription(category, language)}
                    accentColor={accentColor}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <button
            onClick={showSettings ? handleSaveSettings : onAccept}
            style={{
              width: '100%',
              padding: '11px 16px',
              fontSize: '13px',
              fontWeight: 600,
              color: '#ffffff',
              backgroundColor: accentColor,
              border: 'none',
              borderRadius: buttonBorderRadiusValue,
              cursor: 'pointer',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = '0.9')}
            onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
          >
            {showSettings ? texts.saveButton : texts.acceptButton}
          </button>

          <div style={{ display: 'flex', gap: '8px' }}>
            {showSettingsButton && (
              <button
                onClick={() => setShowSettings(!showSettings)}
                style={{
                  flex: 1,
                  padding: '9px 12px',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: showSettings ? accentColor : '#374151',
                  backgroundColor: showSettings ? `${accentColor}10` : '#f3f4f6',
                  border: 'none',
                  borderRadius: buttonBorderRadiusValue,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e =>
                  (e.currentTarget.style.backgroundColor = showSettings
                    ? `${accentColor}15`
                    : '#e5e7eb')
                }
                onMouseLeave={e =>
                  (e.currentTarget.style.backgroundColor = showSettings
                    ? `${accentColor}10`
                    : '#f3f4f6')
                }
              >
                {showSettings ? texts.cancelButton : texts.settingsButton}
              </button>
            )}

            {showRejectLink && !showSettings && (
              <button
                onClick={onDecline}
                style={{
                  flex: 1,
                  padding: '9px 12px',
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#6b7280',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: buttonBorderRadiusValue,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = '#e5e7eb'
                  e.currentTarget.style.color = '#374151'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6'
                  e.currentTarget.style.color = '#6b7280'
                }}
              >
                {texts.declineButton}
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )
}
